import { ReportData } from '../types/reportTypes';
import { codyJanuaryData } from './codyJanuaryData';
import { codyFebruaryData } from './codyFebruaryData';
import { codyMarchData } from './codyMarchData';

// Helper function to sum arrays of numbers
const sum = (numbers: number[]): number => {
  return numbers.reduce((total, num) => total + num, 0);
};

// Combine all job data from Q1 months
const allJobData = [
  ...codyJanuaryData.jobData,
  ...codyFebruaryData.jobData,
  ...codyMarchData.jobData
];

// Calculate combined sales by source
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();
  
  // Combine data from all three months
  const allSalesBySource = [
    ...codyJanuaryData.salesBySource,
    ...codyFebruaryData.salesBySource,
    ...codyMarchData.salesBySource
  ];
  
  allSalesBySource.forEach(source => {
    const key = source.source;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }
    
    const sourceData = sourceMap.get(key);
    sourceData.count += source.count;
    sourceData.totalSales += source.totalSales;
    sourceData.grossProfit += source.grossProfit;
  });
  
  return Array.from(sourceMap.values());
})();

// Calculate combined ad spend by source (all should be 0 for direct sales)
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  allJobData.forEach(job => uniqueSources.add(job.leadSource));
  
  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate combined sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();
  
  // Process all jobs to get trade totals
  allJobData.forEach(job => {
    const key = job.trade;
    const normalizedKey = key === "Siding" ? "Siding" : 
                         key === "Roofing" ? "Roofing" : 
                         key === "Windows & Doors" ? "Windows & Doors" : 
                         "Other";
    
    if (!tradeMap.has(normalizedKey)) {
      tradeMap.set(normalizedKey, {
        trade: normalizedKey,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }
    
    const tradeData = tradeMap.get(normalizedKey);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });
  
  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });
  
  return Array.from(tradeMap.values());
})();

// Calculate Q1 totals by combining all three months
const q1Totals = {
  adSpend: codyJanuaryData.kpis.adSpend + codyFebruaryData.kpis.adSpend + codyMarchData.kpis.adSpend,
  totalSales: codyJanuaryData.kpis.totalSales + codyFebruaryData.kpis.totalSales + codyMarchData.kpis.totalSales,
  grossProfit: codyJanuaryData.kpis.grossProfit + codyFebruaryData.kpis.grossProfit + codyMarchData.kpis.grossProfit,
  salesCommissions: codyJanuaryData.kpis.salesCommissions + codyFebruaryData.kpis.salesCommissions + codyMarchData.kpis.salesCommissions,
  managerFees: codyJanuaryData.kpis.managerFees + codyFebruaryData.kpis.managerFees + codyMarchData.kpis.managerFees,
  companyOH: codyJanuaryData.kpis.companyOH + codyFebruaryData.kpis.companyOH + codyMarchData.kpis.companyOH,
  netProfit: codyJanuaryData.kpis.netProfit + codyFebruaryData.kpis.netProfit + codyMarchData.kpis.netProfit
};

// Export the consolidated Q1 2025 report data
export const codyQ1Data: ReportData = {
  period: {
    month: 3, // Using March as the end month of Q1
    year: 2025
  },
  kpis: {
    adSpend: q1Totals.adSpend,
    totalSales: q1Totals.totalSales,
    grossProfit: q1Totals.grossProfit,
    salesCommissions: q1Totals.salesCommissions,
    managerFees: q1Totals.managerFees,
    companyOH: q1Totals.companyOH,
    netProfit: q1Totals.netProfit,
    netProfitPercent: q1Totals.totalSales > 0 ? q1Totals.netProfit / q1Totals.totalSales : 0,
    grossProfitPercent: q1Totals.totalSales > 0 ? q1Totals.grossProfit / q1Totals.totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData: allJobData
};

// Export monthly breakdown separately for the Q1 component
export const codyQ1MonthlyBreakdown = [
  {
    month: 'January',
    totalSales: codyJanuaryData.kpis.totalSales,
    grossProfit: codyJanuaryData.kpis.grossProfit,
    netProfit: codyJanuaryData.kpis.netProfit,
    jobCount: codyJanuaryData.jobData.length
  },
  {
    month: 'February',
    totalSales: codyFebruaryData.kpis.totalSales,
    grossProfit: codyFebruaryData.kpis.grossProfit,
    netProfit: codyFebruaryData.kpis.netProfit,
    jobCount: codyFebruaryData.jobData.length
  },
  {
    month: 'March',
    totalSales: codyMarchData.kpis.totalSales,
    grossProfit: codyMarchData.kpis.grossProfit,
    netProfit: codyMarchData.kpis.netProfit,
    jobCount: codyMarchData.jobData.length
  }
];